/**
 * SegmentCardParts — presentational subcomponents shared by SegmentCard.
 *
 * Kept separate so SegmentCard stays focused on composition. All parts are
 * stateless views.
 */

import { Minus, Star, TrendingUp } from 'lucide-react'
import type { MarketPersona, MarketSegmentWithScore } from '@ispot/shared'
import {
  SCORE_KEYS,
  formatPercent,
  initialsFor,
  tierBg,
  tierFg,
  tierFor,
  tierSurface,
  type ScoreTier,
} from './segmentCardUtils'

export function BeachheadPill() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-[4px] text-[10px] font-semibold uppercase tracking-[0.04em]"
      style={{ backgroundColor: '#eaf6ff', color: 'var(--color-business, #3196E2)' }}
    >
      <Star className="h-2.5 w-2.5" fill="currentColor" />
      Beachhead
    </span>
  )
}

export function StatusPill({ status }: { status: MarketSegmentWithScore['status'] }) {
  const map = {
    active: { bg: 'var(--color-success-bg, #e0f3e8)', fg: 'var(--color-success-fg, #157243)', label: 'Active' },
    archived: { bg: 'var(--color-info-bg, #e8ebef)', fg: 'var(--color-info-fg, #283646)', label: 'Archived' },
    draft: { bg: 'var(--color-warm-200)', fg: 'var(--color-text-secondary)', label: 'Draft' },
  }[status]
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-[4px] text-[10px] font-semibold uppercase tracking-[0.04em]"
      style={{ backgroundColor: map.bg, color: map.fg }}
    >
      {map.label}
    </span>
  )
}

export function ScoreChip({ score, hasScore, tier }: { score: number; hasScore: boolean; tier: ScoreTier }) {
  const fg = tierFg(tier)
  const surface = tierSurface(tier)

  return (
    <div
      className="flex h-[56px] w-[56px] shrink-0 flex-col items-center justify-center rounded-full font-display tracking-[-0.03em]"
      style={{ backgroundColor: surface }}
      aria-label={hasScore ? `Weighted fit score ${score.toFixed(1)}` : 'No score yet'}
    >
      <span
        className="text-[22px] font-bold leading-none tabular-nums"
        style={{ color: hasScore ? fg : 'var(--color-warm-400)' }}
      >
        {hasScore ? score.toFixed(1) : '—'}
      </span>
      <span
        className="mt-0.5 text-[8px] font-semibold uppercase tracking-[0.1em]"
        style={{ color: hasScore ? fg : 'var(--color-text-muted)' }}
      >
        Fit
      </span>
    </div>
  )
}

export function InlineMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </span>
      <span
        className="font-mono text-[14px] font-medium tabular-nums tracking-[-0.01em]"
        style={{ color: value === '—' ? 'var(--color-warm-400)' : 'var(--color-text)' }}
      >
        {value}
      </span>
    </div>
  )
}

export function CagrChip({ value, trend }: { value: number | null; trend: 'pos' | 'neg' | 'flat' }) {
  const palette =
    trend === 'pos'
      ? { bg: 'var(--color-success-bg, #e0f3e8)', fg: 'var(--color-success-fg, #157243)' }
      : trend === 'neg'
        ? { bg: 'var(--color-danger-bg, #fbdede)', fg: 'var(--color-danger-fg, #a93131)' }
        : { bg: 'var(--color-warm-100)', fg: 'var(--color-text-secondary)' }
  const Icon = trend === 'pos' ? TrendingUp : Minus
  return (
    <div className="ml-auto flex items-baseline gap-2">
      <span className="text-[9px] font-semibold uppercase tracking-[0.12em]" style={{ color: 'var(--color-text-muted)' }}>
        CAGR
      </span>
      <span
        className="inline-flex items-center gap-1 rounded-full px-2.5 py-[4px] text-[12px] font-semibold tabular-nums"
        style={{ backgroundColor: palette.bg, color: palette.fg }}
      >
        <Icon className="h-3 w-3" aria-hidden="true" />
        {formatPercent(value)}
      </span>
    </div>
  )
}

export function FitBreakdown({ segment }: { segment: MarketSegmentWithScore }) {
  return (
    <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
      {SCORE_KEYS.map(({ key, label }) => {
        const v = segment.scoring[key]?.value
        const t = tierFor(v)
        const pct = v == null ? 0 : Math.min(100, (v / 10) * 100)
        return (
          <div
            key={key}
            className="flex min-w-0 flex-col gap-1 py-1"
            title={`${label}${v == null ? ' — not scored' : `: ${v.toFixed(1)} / 10`}`}
          >
            <div className="flex items-baseline justify-between gap-1">
              <span className="truncate text-[10px] font-semibold uppercase tracking-[0.04em]" style={{ color: 'var(--color-text-secondary)' }}>
                {label}
              </span>
              <span
                className="font-mono text-[11px] font-medium tabular-nums"
                style={{ color: v == null ? 'var(--color-warm-400)' : 'var(--color-text)' }}
              >
                {v == null ? '—' : v.toFixed(1)}
              </span>
            </div>
            <div className="h-[3px] overflow-hidden rounded-full" style={{ backgroundColor: 'var(--color-warm-200)' }}>
              <span
                className="block h-full rounded-full transition-[width] duration-300"
                style={{ width: `${pct}%`, backgroundColor: tierBg(t) }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

export function AvatarStack({ personas, overflow }: { personas: MarketPersona[]; overflow: number }) {
  return (
    <span className="inline-flex items-center">
      {personas.map((p, i) => (
        <span
          key={p.id}
          title={p.name}
          className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 text-[9px] font-bold tracking-[-0.02em]"
          style={{
            marginLeft: i === 0 ? 0 : -6,
            backgroundColor: p.role === 'user' ? 'var(--color-market-100, #c9f0f2)' : 'var(--color-business-100, #d6eaf9)',
            color: p.role === 'user' ? 'var(--color-market-700, #15747a)' : 'var(--color-business-600, #1e75b8)',
            borderColor: 'white',
          }}
        >
          {initialsFor(p.name)}
        </span>
      ))}
      {overflow > 0 ? (
        <span
          className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-2 text-[9px] font-bold"
          style={{
            marginLeft: -6,
            backgroundColor: 'var(--color-warm-200)',
            color: 'var(--color-text-secondary)',
            borderColor: 'white',
          }}
        >
          +{overflow}
        </span>
      ) : null}
    </span>
  )
}
